import React, { useState } from 'react';
import { X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPlan: {
    name: string;
    price: string;
    details?: string;
  };
}

const BookingModal: React.FC<BookingModalProps> = ({ isOpen, onClose, selectedPlan }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    message: '',
    consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const isValid = formData.name.trim().length >= 2 && 
                  formData.phone.trim().length >= 10 && 
                  formData.consent;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('/api/send-booking', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          site: 'maestro-174.ru',
          name: formData.name,
          phone: formData.phone,
          message: formData.message,
          plan: selectedPlan.name,
          price: selectedPlan.price,
          details: selectedPlan.details
        })
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        setTimeout(() => {
          onClose();
          setFormData({ name: '', phone: '', message: '', consent: false });
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Ошибка отправки:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={onClose}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="bg-[#1e293b] border border-white/10 rounded-lg p-8 max-w-md w-full relative"
            >
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              <h2 className="text-3xl font-heading font-bold text-white mb-2">
                Записаться
              </h2>
              <p className="text-gray-400 text-sm mb-6">
                Оставьте заявку и мы свяжемся с вами
              </p>

              {/* Selected plan info */}
              <div className="bg-[#0f172a] border border-white/10 p-4 rounded mb-6">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-white font-bold">{selectedPlan.name}</span>
                  <span className="text-[#f97316] font-bold">{selectedPlan.price}</span>
                </div>
                {selectedPlan.details && (
                  <p className="text-gray-400 text-sm">{selectedPlan.details}</p>
                )}
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Ваше имя <span className="text-[#f97316]">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Ваше имя"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-[#0f172a] border border-white/10 px-4 py-3 text-white placeholder:text-gray-500 focus:border-[#f97316] focus:outline-none transition-colors rounded"
                    required
                    minLength={2}
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Телефон <span className="text-[#f97316]">*</span>
                  </label>
                  <input
                    type="tel"
                    placeholder="Телефон"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full bg-[#0f172a] border border-white/10 px-4 py-3 text-white placeholder:text-gray-500 focus:border-[#f97316] focus:outline-none transition-colors rounded"
                    required
                    minLength={10}
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Сообщение
                  </label>
                  <textarea
                    placeholder="Ваше сообщение (необязательно)"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full bg-[#0f172a] border border-white/10 px-4 py-3 text-white placeholder:text-gray-500 focus:border-[#f97316] focus:outline-none transition-colors rounded resize-none"
                    rows={3}
                  />
                </div>

                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="consent"
                    checked={formData.consent}
                    onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
                    className="mt-1 w-4 h-4 accent-[#f97316]"
                    required
                  />
                  <label htmlFor="consent" className="text-sm text-gray-400">
                    Я согласен на обработку персональных данных <span className="text-[#f97316]">*</span>
                  </label>
                </div>

                {submitStatus === 'success' && (
                  <div className="bg-green-500/20 border border-green-500 rounded p-3 text-green-400 text-sm">
                    Заявка успешно отправлена! Мы свяжемся с вами в ближайшее время.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-500/20 border border-red-500 rounded p-3 text-red-400 text-sm">
                    Ошибка отправки. Попробуйте позже или позвоните нам.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!isValid || isSubmitting}
                  className="w-full bg-[#f97316] text-black font-bold uppercase tracking-widest py-3 rounded hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Отправка...' : 'Отправить заявку'}
                </button>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default BookingModal;
